import { v4 as uuid_v4 } from 'uuid';

import { getRegionByCurrency } from '@luxuryescapes/lib-regions';

import { LE_BUSINESS_TRAVELLER } from '~/consts/businessTraveller';
import { UTM_MEDIUM } from '~/consts/utm';

import { getEmployee } from '~/services/BusinessTraveller/BusinessTravellerService';

import { buildServiceFeeOrderItem } from '~/utils/businessTraveller/serviceFee';

import { CRUISE_ITEM_TYPE, POST_PURCHASE_TYPE_NAME_CORRECTION, PURCHASE_TYPE_POST_PURCHASE } from './constants';
import { buildPendingPointsPayload, fetchLoyaltyTier } from './loyaltyUtils';

import CustomOfferType = App.CustomOfferType;

type BuildCustomOfferPaymentPayloadV2Options = {
  brand?: App.Brands;
  supportManualFlow?: boolean;
  includeBusinessId?: boolean;
};

export async function buildCustomOfferPaymentPayloadV2(
  user: App.User,
  offer: App.CustomOffer,
  {
    brand = 'luxuryescapes',
    supportManualFlow = false,
    includeBusinessId = false,
  }: BuildCustomOfferPaymentPayloadV2Options = {},
) {
  const transactionKey = uuid_v4();
  const total =
    supportManualFlow && offer.payment_metadata.payment_flow_v2?.type === 'manual'
      ? (offer.payment_metadata.payment_flow_v2.manual_balance_amount ?? 0)
      : offer.price / 100;
  const gstAmount = offer.payment_metadata.gst_amount ? offer.payment_metadata.gst_amount / 100 : 0;
  const vendorTotal = offer.payment_metadata.final_amount_vendor / 100;
  const regionCode = getRegionByCurrency(offer.price_currency, brand);

  const employee = includeBusinessId && brand === LE_BUSINESS_TRAVELLER ? await getEmployee(user.id_member) : undefined;

  // Cruise amendments and name corrections may charge only an LE fee, with no vendor cost.
  const allowsZeroVendorTotal =
    offer.purchase_type === PURCHASE_TYPE_POST_PURCHASE &&
    (offer.items[0]?.type === CRUISE_ITEM_TYPE || offer.post_purchase_type === POST_PURCHASE_TYPE_NAME_CORRECTION);

  if (total <= 0 || vendorTotal < 0 || (vendorTotal === 0 && !allowsZeroVendorTotal)) {
    throw new Error('Customer and Vendor total amount must be greater than 0');
  }

  const pendingPoints = await buildPendingPointsPayload(
    user,
    offer,
    transactionKey,
    offer.payment_metadata?.sales_attribution,
    brand,
  );
  const tier = pendingPoints ? await fetchLoyaltyTier(user.id_member) : null;
  const serviceFeeItem = brand === LE_BUSINESS_TRAVELLER ? buildServiceFeeOrderItem(offer.payment_metadata) : null;

  return {
    brand,
    business_id: employee?.businessId ?? undefined,
    currency_code: offer.price_currency,
    region_code: regionCode.code,
    customer_id: user.id_member,
    customer_email: user.email,
    customer_phone_prefix: offer.customer_phone_prefix,
    customer_phone: offer.customer_phone_number,
    utm_source: brand,
    utm_medium: offer?.payment_metadata?.sales_attribution ?? UTM_MEDIUM,
    transaction_key: transactionKey,
    type: 'custom_offer',
    ...(brand === LE_BUSINESS_TRAVELLER && {
      concierge_mode: offer.payment_metadata.lebt_service_fee?.concierge_mode ?? false,
    }),
    items: [
      ...offer.items.map((item) => ({
        ...item,
        transaction_key: transactionKey,
        type: 'custom_offer' as unknown as CustomOfferType,
        custom_offer_id: offer.id,
        due_date: offer.due_date,
        total,
        gst_amount: gstAmount,
        custom_item_type: item.type,
      })),
      ...(serviceFeeItem ? [serviceFeeItem] : []),
    ],
    ...(pendingPoints && tier && { lux_loyalty: { pendingPoints, tier } }),
  };
}
